/**
 * Photo import normalization (docs/spec/01 §9): EXIF orientation applied, long edge capped at
 * 3072 px, re-encoded as JPEG 0.93. Also makes a 512 px gallery thumbnail.
 */
export const MAX_LONG_EDGE = 3072;
export const THUMB_LONG_EDGE = 512;
export const PHOTO_ERROR = 'Could not open that photo.';

export interface NormalizedPhoto {
  photo: Blob;
  width: number;
  height: number;
  thumb: Blob;
  thumbWidth: number;
  thumbHeight: number;
}

export function fitLongEdge(w: number, h: number, max: number): [number, number] {
  const long = Math.max(w, h);
  if (long <= max) return [w, h];
  const k = max / long;
  return [Math.max(1, Math.round(w * k)), Math.max(1, Math.round(h * k))];
}

async function encode(source: ImageBitmap, w: number, h: number, quality: number): Promise<Blob> {
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(PHOTO_ERROR);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  return canvas.convertToBlob({ type: 'image/jpeg', quality });
}

export async function normalizePhoto(file: Blob): Promise<NormalizedPhoto> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error(PHOTO_ERROR);
  }
  try {
    const [w, h] = fitLongEdge(bitmap.width, bitmap.height, MAX_LONG_EDGE);
    const [tw, th] = fitLongEdge(bitmap.width, bitmap.height, THUMB_LONG_EDGE);
    const photo = await encode(bitmap, w, h, 0.93);
    const thumb = await encode(bitmap, tw, th, 0.85);
    return { photo, width: w, height: h, thumb, thumbWidth: tw, thumbHeight: th };
  } finally {
    bitmap.close();
  }
}

/**
 * Decodes a stored photo for the renderer as raw sRGB-encoded values: no color-space
 * conversion, no premultiplication (docs/spec/03 §1.1).
 */
export function decodeRaw(blob: Blob): Promise<ImageBitmap> {
  return createImageBitmap(blob, {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
    imageOrientation: 'from-image',
  });
}
